import { findTimeSuggestions, isIgnored, type IgnoredTerm, type TimeSuggestion } from '@dispositio/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { PluginKey } from '@tiptap/pm/state';
import { suggestExtension, type SuggestAct, type SuggestLive, type SuggestState } from './suggest';

/**
 * Mögliche Zeitangaben im Texteditor (PLAN.md §16): Was wie eine Zeit klingt („drei Tage später“, „am 3. März 1823“) und noch keine
 * Zeitmarke ist, ist als Vorschlag markiert (`suggest.ts`); ✓ setzt die Zeitmarke, ✕ lässt die Stelle in Ruhe (gemerkt wie bei den
 * möglichen Verweisen).
 */

export type TimeAct = SuggestAct;

export interface TimeLive extends SuggestLive<TimeSuggestion> {
  /** bewusst nicht markierte Stellen dieses Dokuments */
  ignored: readonly IgnoredTerm[] | undefined;
  title: (hit: TimeSuggestion) => string;
}

export const timeSuggestKey = new PluginKey<SuggestState<TimeSuggestion>>('timeSuggest');

/** Vorschläge im Dokument (mit ihrer Lage im Dokument): je Absatz, ohne Stellen, die schon eine Zeitmarke sind; ohne ignorierte. */
export function timeHits(doc: PMNode, ignored: readonly IgnoredTerm[] | undefined): TimeSuggestion[] {
  const out: TimeSuggestion[] = [];
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    if (node.type.spec.code) return false;
    const text = node.textBetween(0, node.content.size, '\n', '\n');
    const marked: [number, number][] = [];
    node.forEach((child, offset) => {
      if (child.isText && child.marks.some((m) => m.type.name === 'timeMark')) marked.push([offset, offset + child.nodeSize]);
    });
    for (const s of findTimeSuggestions(text, marked)) if (!isIgnored({ term: s.text, before: s.before, after: s.after }, ignored)) out.push({ ...s, from: pos + 1 + s.from, to: pos + 1 + s.to });
    return false;
  });
  return out;
}

export const timeSuggest = (live: { current: TimeLive }) =>
  suggestExtension<TimeSuggestion, TimeLive>(
    {
      name: 'timeSuggest',
      kind: 'time',
      key: timeSuggestKey,
      hits: (doc, l) => timeHits(doc, l.ignored),
      mark: (h, l) => ({ class: 'rt-time-suggest', 'data-time-suggest': h.value, title: l.title(h) }),
      word: (h) => h.text,
      testId: 'time',
    },
    live,
  );
